import {
    TermLength,
    InvestmentBuffer,
    InvestmentIndex,
    InvestmentStrategyType,
    buildInvestmentIndexMap,
    buildTermLengthMap,
    buildInvestmentBufferMap,
    buildInvestmentStrategyTypeMap
} from "./CalculatorInputs";
import { left, right, Either, fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/pipeable";
import { some, none, Option } from "fp-ts/lib/Option";


export const terms = buildTermLengthMap();
export const strategyTypes = buildInvestmentStrategyTypeMap();
export const buffers = buildInvestmentBufferMap();
export const indices = buildInvestmentIndexMap();

export class AvailableStrategy {

    readonly termLength: TermLength;
    readonly strategyType: InvestmentStrategyType;
    readonly buffer: InvestmentBuffer;
    readonly index: InvestmentIndex;
    readonly productCode: string;

    constructor(termLength: TermLength,
        strategyType: InvestmentStrategyType,
        buffer: InvestmentBuffer,
        index: InvestmentIndex,
        productCode: string) {
        this.termLength = termLength;
        this.strategyType = strategyType;
        this.buffer = buffer;
        this.index = index;
        this.productCode = productCode;
    }

    static build(term: number, typeCode: string, buffer: number, indexCode: string, productCode: string): Either<Error, AvailableStrategy> {
        const errors = [];
        if (!terms.has(term)) {
            errors.push(`Unknown term length: ${term} years`);
        }
        if (!strategyTypes.has(typeCode)) {
            errors.push(`Unknown investment strategy type code: ${typeCode}`);
        }
        if (!buffers.has(buffer)) {
            errors.push(`Unknown buffer percentage: ${buffer}%`);
        }
        if (!indices.has(indexCode)) {
            errors.push((`Unknown investment index code: ${indexCode}`));
        }
        if (errors.length > 0) {
            return left(new Error(errors.join(";\n")))
        } else {
            const t = terms.get(term);
            const s = strategyTypes.get(typeCode);
            const b = buffers.get(buffer);
            const i = indices.get(indexCode);
            if (t && s && b && i) {
                return right(new AvailableStrategy(t, s, b, i, productCode));
            } else {
                return left(new Error(`Could not create strategy from data: ${term}, ${typeCode}, ${buffer}, ${indexCode}`))
            }
        }


    }

}

export interface AvailableStrategyConfig {
    termLength: number;
    strategyType: string;
    buffer: number;
    index: string;
    productCode: string;
}
export function buildFromConfig(cfg: AvailableStrategyConfig) {
    return AvailableStrategy.build(cfg.termLength, cfg.strategyType, cfg.buffer, cfg.index, cfg.productCode);
}

export class AvailableInvestmentStrategies {
    readonly strategies: Array<AvailableStrategy>;
    readonly errors: Array<Error>;
    constructor(configs: Array<AvailableStrategyConfig>) {
        this.strategies = [];
        this.errors = [];
        const possibleStrategies: Array<Either<Error, AvailableStrategy>> = [];
        for (const cfg of configs) {
            possibleStrategies.push(buildFromConfig(cfg));
        }

        for (const e of possibleStrategies) {
            pipe(e,
                fold((err) => this.errors.push(err),
                    (strat) => this.strategies.push(strat))
            );
        }
    }

    strategiesForTerm(term: TermLength): Array<AvailableStrategy> {
        return this.strategies.filter(s => s.termLength === term);
    }
    strategiesForTermAndType(term: TermLength, st: InvestmentStrategyType): Array<AvailableStrategy> {
        return this.strategiesForTerm(term).filter(s => s.strategyType === st);
    }
    strategiesForTermAndTypeAndBuffer(term: TermLength, st: InvestmentStrategyType, buffer: InvestmentBuffer): Array<AvailableStrategy> {
        return this.strategiesForTermAndType(term, st).filter(s => s.buffer === buffer);
    }

    strategyForTermAndTypeAndBufferAndIndex(term: TermLength,
        st: InvestmentStrategyType,
        buffer: InvestmentBuffer,
        index: InvestmentIndex): Option<AvailableStrategy> {

        const strategies = this.strategiesForTermAndTypeAndBuffer(term, st, buffer).filter(s => s.index === index);
        if (strategies.length === 0) {
            return none;
        } else {
            if (strategies.length > 1) {
                console.log(`Validation Warning: More than one strategy for for details: ${term.years} | ${st.code} | ${buffer.code} | ${index.code}`);
            }
            return some(strategies[0]);
        }
    }
}